import '@theme/dialog';

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.track = this.querySelector('[data-gallery-track]');
    this.media = [...this.querySelectorAll('[data-gallery-media]')];
    this.thumbnails = [...this.querySelectorAll('[data-gallery-thumbnail]')];
    this.zoomTriggers = [...this.querySelectorAll('[data-gallery-zoom]')];
    this.current = this.querySelector('[data-gallery-current]');
    this.previous = this.querySelector('[data-gallery-previous]');
    this.next = this.querySelector('[data-gallery-next]');
    this.thumbnailGroup = this.querySelector('[data-gallery-thumbnails]');
    this.zoomDialog = this.querySelector('[data-gallery-modal]');
    this.zoomImage = this.querySelector('[data-gallery-dialog-image]');
    this.zoomCurrent = this.querySelector('[data-gallery-dialog-current]');
    this.zoomPrevious = this.querySelector('[data-gallery-dialog-previous]');
    this.zoomNext = this.querySelector('[data-gallery-dialog-next]');

    this.previous?.addEventListener('click', this.showPrevious, { signal });
    this.next?.addEventListener('click', this.showNext, { signal });
    this.track?.addEventListener('scroll', this.onScroll, { passive: true, signal });
    this.track?.addEventListener('keydown', this.onTrackKeydown, { signal });
    this.thumbnailGroup?.addEventListener('click', this.onThumbnailClick, { signal });
    this.thumbnailGroup?.addEventListener('keydown', this.onThumbnailKeydown, { signal });
    this.zoomPrevious?.addEventListener('click', this.showPreviousZoom, { signal });
    this.zoomNext?.addEventListener('click', this.showNextZoom, { signal });
    this.querySelector('dialog')?.addEventListener('keydown', this.onZoomKeydown, { signal });
    for (const trigger of this.zoomTriggers) trigger.addEventListener('click', this.openZoom, { signal });
    this.update(0);
  }

  disconnectedCallback() {
    this.controller?.abort();
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  index = 0;
  zoomIndex = 0;
  showPrevious = () => this.goTo(this.index - 1);
  showNext = () => this.goTo(this.index + 1);

  onThumbnailClick = (event) => {
    const thumbnail = event.target.closest('[data-gallery-thumbnail]');
    if (!(thumbnail instanceof HTMLElement)) return;
    event.preventDefault();
    this.goTo(Number(thumbnail.dataset.index));
  };

  onThumbnailKeydown = (event) => {
    const thumbnail = event.target.closest('[data-gallery-thumbnail]');
    if (!(thumbnail instanceof HTMLElement)) return;

    const currentIndex = Number(thumbnail.dataset.index);
    let nextIndex;
    if (event.key === 'ArrowLeft') nextIndex = currentIndex - 1;
    if (event.key === 'ArrowRight') nextIndex = currentIndex + 1;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.thumbnails.length - 1;
    if (nextIndex === undefined) return;

    event.preventDefault();
    nextIndex = Math.max(0, Math.min(nextIndex, this.thumbnails.length - 1));
    this.goTo(nextIndex);
    this.thumbnails[nextIndex]?.focus();
  };

  onTrackKeydown = (event) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    if (event.key === 'ArrowLeft') this.goTo(this.index - 1);
    if (event.key === 'ArrowRight') this.goTo(this.index + 1);
    if (event.key === 'Home') this.goTo(0);
    if (event.key === 'End') this.goTo(this.media.length - 1);
  };

  onScroll = () => {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => {
      let nextIndex = 0;
      let distance = Number.POSITIVE_INFINITY;
      for (const [index, media] of this.media.entries()) {
        const current = Math.abs(media.offsetLeft - this.track.scrollLeft);
        if (current >= distance) continue;
        distance = current;
        nextIndex = index;
      }
      this.update(nextIndex);
    });
  };

  goTo(index, behavior) {
    if (!this.track || !this.media.length) return;
    const nextIndex = Math.max(0, Math.min(index, this.media.length - 1));
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({
      left: this.media[nextIndex].offsetLeft,
      behavior: behavior || (reduced ? 'auto' : 'smooth'),
    });
    this.update(nextIndex);
  }

  goToMedia(mediaId) {
    const index = this.media.findIndex((item) => item.dataset.mediaId === String(mediaId));
    if (index >= 0) this.goTo(index, 'auto');
  }

  update(index) {
    this.index = Math.max(0, Math.min(index, Math.max(0, this.media.length - 1)));
    if (this.current) this.current.textContent = String(this.index + 1);
    for (const [thumbnailIndex, thumbnail] of this.thumbnails.entries()) {
      thumbnail.toggleAttribute('aria-current', thumbnailIndex === this.index);
    }
    for (const [mediaIndex, media] of this.media.entries()) {
      if (mediaIndex !== this.index) media.querySelector('deferred-media')?.pause?.();
    }
    if (this.previous) this.previous.disabled = this.index === 0;
    if (this.next) this.next.disabled = this.index === this.media.length - 1;
  }

  openZoom = (event) => {
    if (!this.zoomDialog || typeof this.zoomDialog.open !== 'function') return;
    event.preventDefault();
    this.zoomIndex = this.zoomTriggers.indexOf(event.currentTarget);
    this.updateZoom();
    this.zoomDialog.open(event.currentTarget);
  };

  showPreviousZoom = () => this.showZoom(this.zoomIndex - 1);
  showNextZoom = () => this.showZoom(this.zoomIndex + 1);

  showZoom(index) {
    if (!this.zoomTriggers.length) return;
    this.zoomIndex = (index + this.zoomTriggers.length) % this.zoomTriggers.length;
    this.updateZoom();
  }

  updateZoom() {
    const trigger = this.zoomTriggers[this.zoomIndex];
    if (!trigger || !this.zoomImage) return;
    this.zoomImage.src = trigger.dataset.galleryZoomSrc;
    this.zoomImage.alt = trigger.dataset.galleryZoomAlt || '';
    if (this.zoomCurrent) this.zoomCurrent.textContent = String(this.zoomIndex + 1);
  }

  onZoomKeydown = (event) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    if (event.key === 'ArrowLeft') this.showPreviousZoom();
    if (event.key === 'ArrowRight') this.showNextZoom();
    if (event.key === 'Home') this.showZoom(0);
    if (event.key === 'End') this.showZoom(this.zoomTriggers.length - 1);
  };
}

if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);
